import type { ReactNode } from "react";
import { extractFieldErrors } from "@shared-core/data/validation";
import { Input } from "../../app/components/primitives/input";
import { Label } from "../../app/components/primitives/label";

export type FieldErrorMap = Record<string, string>;

/** The first message per field from a failed save, keyed by request-body field name. */
export function toFieldErrorMap(error: unknown): FieldErrorMap {
  const map: FieldErrorMap = {};
  for (const fieldError of extractFieldErrors(error)) {
    if (!(fieldError.field in map)) map[fieldError.field] = fieldError.message;
  }
  return map;
}

interface FieldShellProps {
  id: string;
  label: string;
  error?: string;
  hint?: string;
  children: (aria: {
    "aria-invalid"?: true;
    "aria-describedby"?: string;
  }) => ReactNode;
}

/** Label, control and the field's own error or hint, wired for screen readers. */
export function FieldShell({
  id,
  label,
  error,
  hint,
  children,
}: FieldShellProps) {
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children({
        "aria-invalid": error ? true : undefined,
        "aria-describedby": describedBy,
      })}
      {error ? (
        <p
          id={`${id}-error`}
          className="text-sm text-red-600 dark:text-red-400"
        >
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-xs text-muted-foreground">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

interface TextFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  hint?: string;
  placeholder?: string;
  inputMode?: "text" | "numeric" | "decimal";
}

export function TextField({
  id,
  label,
  value,
  onChange,
  error,
  hint,
  placeholder,
  inputMode,
}: TextFieldProps) {
  return (
    <FieldShell id={id} label={label} error={error} hint={hint}>
      {(aria) => (
        <Input
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          inputMode={inputMode}
          {...aria}
        />
      )}
    </FieldShell>
  );
}

export const selectClassName =
  "w-full h-9 rounded-md border border-border bg-background px-3 text-sm disabled:opacity-50";
